import dayjs, { Dayjs } from 'dayjs';

export interface ITime {
  hour: number
  minute: number
  toString: () => string
}

export class Time implements ITime {
  public readonly hour: number;
  public readonly minute: number;

  constructor (hour: number, minute: number) {
    this.hour = hour;
    this.minute = minute;
  }

  public addMinutes (interval: number): Time {
    const newMinute = this.minute + interval;
    const newHour = this.hour + Math.floor(newMinute / 60);
    return new Time(newHour, newMinute % 60);
  }

  public isBefore (endTime: Time): boolean {
    if (this.hour < endTime.hour) { return true; }
    if (this.hour === endTime.hour && this.minute < endTime.minute) { return true; }
    return false;
  }

  public toDayJsFromDateString (dateString: string): Dayjs {
    // validate dateString is in format YYYY-MM-DD
    if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/) || !dayjs(dateString).isValid()) {
      throw new Error('Invalid date string');
    }
    return dayjs(dateString).hour(this.hour).minute(this.minute);
  }

  public static toDayJsFromTime (time: ITime): Dayjs {
    return dayjs().hour(time.hour).minute(time.minute);
  }

  public toString (): string {
    let readableHour = this.hour > 12 ? this.hour - 12 : this.hour;
    if (readableHour === 0) { readableHour = 12; }
    const hour = readableHour < 10 ? `0${readableHour}` : `${readableHour}`;
    const minute = this.minute < 10 ? `0${this.minute}` : `${this.minute}`;
    const ampm = this.hour < 12 ? 'AM' : 'PM';
    return `${hour}:${minute} ${ampm}`;
  }

  public static fromString (timeString: string): Time {
    const isAM = timeString.includes('AM');
    const [hourString, minuteString] = timeString.split(':');
    const hour = parseInt(hourString);
    const minute = parseInt(minuteString);
    if (isAM && hour === 12) { return new Time(0, minute); }
    if (!isAM && hour !== 12) { return new Time(hour + 12, minute); }
    return new Time(hour, minute);
  }

  static fromTime (time: ITime): Time {
    return new Time(time.hour, time.minute);
  }

  static getTimeslots (startTime: Time, endTime: Time, interval: number): Time[] {
    const timeslots: Time[] = [];
    let currentTime = startTime;
    while (currentTime.isBefore(endTime)) {
      timeslots.push(currentTime);
      currentTime = currentTime.addMinutes(interval);
    }
    return timeslots;
  }

  static getWholeDayTimeslots (interval: number): Time[] {
    return Time.getTimeslots(new Time(0, 0), new Time(24, 0), interval);
  }

  static getDatesBetween (startDate: Date | Dayjs, endDate: Date | Dayjs, interval: number = 30): Dayjs[] {
    const dates: Dayjs[] = [];
    let currentDate = dayjs(startDate);
    while (currentDate.isBefore(endDate)) {
      dates.push(currentDate);
      currentDate = currentDate.add(interval, 'minute');
    }
    if (currentDate.isSame(endDate)) {
      dates.push(currentDate);
    }
    return dates;
  }
}
